import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, FileText, ChevronDown } from 'lucide-react';
import { api } from '../services/apiService';

interface AuditEntry {
  auditId: string;
  eventTimestamp: string;
  actor: string;
  action: string;
  entity: string;
  entityId: string;
  before?: any;
  after?: any;
  ipAddress?: string;
  timestamp: number;
  createdAt: string;
}

const ENTITY_FILTERS = [
  { value: '', label: 'All entities' },
  { value: 'register', label: 'Payment Types' },
  { value: 'emv-device', label: 'EMV Device' },
  { value: 'payment-gateway-config', label: 'Gateway Config' },
];

const AuditLogPage: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const entity = params.get('entity') || '';

  const fetchAudit = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      setError(null);
      const resp = await api.posNmi.getAuditLog(tenantId, entity || undefined, 200);
      setItems(resp?.items || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [tenantId, entity]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  return (
    <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
      <div className="bg-black px-4 py-4 md:py-5">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3">
          <ChevronLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gray-800 border border-gray-700 shrink-0">
            <FileText size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white text-base md:text-lg font-semibold">Payment Config Audit Log</h1>
            <p className="text-gray-400 text-xs md:text-sm mt-0.5 truncate font-mono">{tenantId}</p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-4">
        <div className="bg-white rounded-xl shadow-sm border border-stone-200/60 p-3">
          <select
            value={entity}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              if (e.target.value) next.set('entity', e.target.value); else next.delete('entity');
              setParams(next);
            }}
            className="px-3 py-2 text-sm border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
          >
            {ENTITY_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {loading && (
          <RubyLoader variant="page" />
        )}

        {error && (
          <div className="bg-white rounded-xl border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200/60 overflow-hidden">
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-stone-500">No audit entries yet.</div>
            ) : (
              <div className="divide-y divide-stone-100">
                {items.map((a) => {
                  const isOpen = expanded === a.auditId;
                  return (
                    <div key={a.auditId}>
                      <button
                        onClick={() => setExpanded(isOpen ? null : a.auditId)}
                        className="w-full text-left px-4 py-3 hover:bg-stone-50 flex items-center gap-3"
                      >
                        <ChevronDown size={14} className={`text-stone-400 shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-stone-800">{a.action}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-600 border border-stone-200">
                              {a.entity}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500 mt-0.5 font-mono truncate">
                            {a.entityId} · by {a.actor}
                            {a.ipAddress ? ` · ${a.ipAddress}` : ''}
                          </p>
                        </div>
                        <span className="text-xs text-stone-500 font-mono shrink-0">{formatDate(a.createdAt || a.timestamp)}</span>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 pt-1 bg-stone-50 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <DiffPanel title="Before" data={a.before} />
                          <DiffPanel title="After" data={a.after} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DiffPanel: React.FC<{ title: string; data: any }> = ({ title, data }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">{title}</p>
    <pre className="bg-stone-900 text-emerald-300 text-[11px] rounded-lg p-3 overflow-x-auto font-mono max-h-64">
      {data ? JSON.stringify(data, null, 2) : '—'}
    </pre>
  </div>
);

function formatDate(d: string | number): string {
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

export default AuditLogPage;
